import { useEffect, useRef, useState } from "react";
import { api, formatarErroApi, API_BASE } from "@/api";
import { LayoutAutenticado } from "@/componentes/LayoutAutenticado";
import { toast } from "sonner";
import { X, Upload } from "lucide-react";

export default function GerenciarPagamentos() {
  const [lista, setLista] = useState([]);
  const [modal, setModal] = useState(null);

  const carregar = async () => {
    const { data } = await api.get("/pagamentos");
    setLista(data);
  };
  useEffect(() => { carregar(); }, []);

  return (
    <LayoutAutenticado>
      <div data-testid="pagina-pagamentos">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Financeiro</div>
          <h1 className="font-heading text-4xl font-bold uppercase mt-1">Controle de Pagamentos</h1>
        </div>

        <div className="surface-card rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-white/50 font-bold">
              <tr>
                <th className="px-4 py-3 text-left">Apostador</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Valor</th>
                <th className="px-4 py-3 text-left">Data Pagamento</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {lista.map((p) => (
                <tr key={p.id} data-testid={`linha-pagamento-${p.id}`}>
                  <td className="px-4 py-3 font-bold">{p.usuario?.nomeCompleto || "—"}</td>
                  <td className="px-4 py-3 text-white/70">{p.usuario?.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-[10px] uppercase tracking-widest font-bold ${p.statusPagamento === "PAGO" ? "bg-verdebrasil/20 text-verdebrasil" : "bg-amarelobrasil/20 text-amarelobrasil"}`}>{p.statusPagamento}</span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{p.valorPago ? `R$ ${Number(p.valorPago).toFixed(2)}` : "—"}</td>
                  <td className="px-4 py-3 text-white/70">{p.dataPagamento ? new Date(p.dataPagamento).toLocaleDateString("pt-BR") : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setModal(p)}
                      className="text-xs font-bold uppercase tracking-widest text-amarelobrasil hover:text-white"
                      data-testid={`btn-editar-pag-${p.id}`}
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && <ModalPagamento pag={modal} onFechar={() => setModal(null)} onSalvo={() => { setModal(null); carregar(); }} />}
    </LayoutAutenticado>
  );
}

function ModalPagamento({ pag, onFechar, onSalvo }) {
  const inputRef = useRef(null);
  const [form, setForm] = useState({
    statusPagamento: pag.statusPagamento,
    valorPago: pag.valorPago || "",
    dataPagamento: pag.dataPagamento ? pag.dataPagamento.slice(0, 10) : "",
    observacao: pag.observacao || "",
  });
  const [arquivo, setArquivo] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const submeter = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      const formData = new FormData();
      formData.append("statusPagamento", form.statusPagamento);
      if (form.valorPago) formData.append("valorPago", Number(form.valorPago));
      if (form.dataPagamento) formData.append("dataPagamento", new Date(form.dataPagamento).toISOString());
      if (form.observacao) formData.append("observacao", form.observacao);
      if (arquivo) formData.append("comprovante", arquivo);

      const token = localStorage.getItem("bolao_token");
      const resp = await fetch(`${API_BASE}/pagamentos/${pag.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data?.detail || "Erro ao atualizar pagamento");
      }
      
      toast.success("Pagamento atualizado");
      onSalvo();
    } catch (e) { toast.error(e.message || formatarErroApi(e)); }
    finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <form onSubmit={submeter} className="surface-card rounded-2xl p-8 max-w-lg w-full relative space-y-4 max-h-[90vh] overflow-y-auto" data-testid="modal-pagamento">
        <button type="button" onClick={onFechar} className="absolute top-4 right-4 text-white/60 hover:text-white"><X className="w-5 h-5"/></button>
        <h2 className="font-heading uppercase font-bold text-xl">Editar Pagamento</h2>
        <div className="text-sm text-white/60">{pag.usuario?.nomeCompleto}</div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Status</label>
          <select
            value={form.statusPagamento}
            onChange={(e) => setForm({ ...form, statusPagamento: e.target.value })}
            className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-4 py-3 text-sm"
            data-testid="select-status-pagamento"
          >
            <option value="PENDENTE">Pendente</option>
            <option value="PAGO">Pago</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Valor pago (R$)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.valorPago}
            onChange={(e) => setForm({ ...form, valorPago: e.target.value })}
            className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-4 py-3 text-sm"
            data-testid="input-valor-pago"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Data pagamento</label>
          <input
            type="date"
            value={form.dataPagamento}
            onChange={(e) => setForm({ ...form, dataPagamento: e.target.value })}
            className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-4 py-3 text-sm"
            data-testid="input-data-pagamento"
          />
        </div>
        
        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold mb-2 block">Comprovante</label>
          <div
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-white/10 hover:border-verdebrasil/60 rounded-lg p-6 text-center cursor-pointer transition-colors"
            data-testid="dropzone-comprovante"
          >
            <Upload className="w-6 h-6 mx-auto text-white/40 mb-2" />
            <div className="text-sm">
              {arquivo ? (
                <>
                  <span className="font-bold text-verdebrasil">{arquivo.name}</span>
                  <div className="text-xs text-white/40 mt-1">{(arquivo.size / 1024).toFixed(1)} KB · clique para trocar</div>
                </>
              ) : (
                <>
                  <span className="font-bold">Clique para enviar o comprovante</span>
                  <div className="text-xs text-white/40 mt-1">Imagem (JPG, PNG) ou PDF</div>
                </>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              onChange={(e) => setArquivo(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="input-comprovante"
            />
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Observação</label>
          <textarea
            rows={3}
            value={form.observacao}
            onChange={(e) => setForm({ ...form, observacao: e.target.value })}
            className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-4 py-3 text-sm"
            data-testid="input-observacao"
          />
        </div>
        <button
          type="submit"
          disabled={enviando}
          className="w-full py-3 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark disabled:opacity-50"
          data-testid="btn-salvar-pagamento"
        >
          {enviando ? "Salvando..." : "Salvar"}
        </button>
      </form>
    </div>
  );
}
